/**
 * CommissionTabContent.tsx
 * Commission tab content for the fee configuration editor.
 *
 * Renders the commission editing form (edit mode) or read-only display (inherited mode).
 * Supports all 8 commission types with dynamic field rendering, tier management,
 * and verification section.
 *
 * This is a controlled component — the parent (FeeConfigurationEditor) owns the
 * commission state and passes it via props. Changes are communicated via onChange.
 */

import React, { useCallback } from 'react';
import { Plus, Trash2, ShieldCheck, NotepadText, Settings2 } from 'lucide-react';

// Workspace packages
import {
  Select,
  SelectItem,
  NumberInput,
  Input,
  TextArea,
  Button,
  Chip,
  Card,
  CardHeader,
  CardBody,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@enfomentis/ui';
import type { Selection } from '@react-types/shared';

// Internal absolute imports (type-only)
import type { CommissionType, CommissionTiming } from '@/types/shared/FeeEnums';
import type { CommissionTier } from '@/types/shared/feeStructures/CommissionStructure';

// Internal absolute imports
import {
  COMMISSION_TYPE_DISPLAY_MAP,
  COMMISSION_TIMING_DISPLAY_MAP,
  VERIFICATION_STATUS_DISPLAY_MAP,
} from '@/constants/feeEnums';
import { needsVerification } from '@/utils/feeEnums';
import { toDisplayValue, toStorageValue } from '@/utils/moneyHelpers';

// Relative imports
import type { CommissionTabContentProps, LoosePartial } from './feeEditorTypes';
import {
  TYPES_WITH_FEE_AMOUNT,
  FEE_AMOUNT_LABEL_MAP,
  COMMISSION_TIMING_DESCRIPTION_MAP,
  VERIFICATION_STATUS_COLOR_MAP,
  formatFeeAmount,
  buildSwitchedCommission,
  rescaleForPrecision,
} from './feeEditorUtils';

// =============================================================================
// Constants
// =============================================================================

/** Commission types that show chargeOn, min/max per order fields */
const TYPES_WITH_ORDER_FIELDS = new Set<CommissionType>([
  'fixedPerUnit',
  'fixedPerTransaction',
  'percentage',
  'perShare',
  'perSharePlusBase',
  'tiered',
  'makerTaker',
]);

/** Precision dropdown options (0–6 decimal places) */
const PRECISION_OPTIONS = [
  { value: 0, label: '0 — (1)' },
  { value: 1, label: '1 — (0.1)' },
  { value: 2, label: '2 — (0.01)' },
  { value: 3, label: '3 — (0.001)' },
  { value: 4, label: '4 — (0.0001)' },
  { value: 5, label: '5 — (0.00001)' },
  { value: 6, label: '6 — (0.000001)' },
] as const;

// =============================================================================
// Component
// =============================================================================

export const CommissionTabContent: React.FC<CommissionTabContentProps> = ({
  commission,
  mode,
  accountCurrency,
  onChange,
  hideVerification,
}) => {
  // ---------------------------------------------------------------------------
  // Change Helpers
  // ---------------------------------------------------------------------------

  const emitChange = useCallback(
    (partial: LoosePartial<typeof commission>): void => {
      if (!onChange) return;
      const next: Record<string, unknown> = { ...commission };
      for (const [key, value] of Object.entries(partial)) {
        if (value === undefined) {
          delete next[key];
        } else {
          next[key] = value;
        }
      }
      onChange(next as unknown as typeof commission);
    },
    [commission, onChange]
  );

  // ---------------------------------------------------------------------------
  // Select Handlers
  // ---------------------------------------------------------------------------

  const handleTypeChange = useCallback(
    (keys: Selection): void => {
      if (keys === 'all' || !onChange) return;
      const newType = Array.from(keys)[0] as CommissionType | undefined;
      if (!newType || newType === commission.feeType) return;
      onChange(buildSwitchedCommission(commission, newType));
    },
    [commission, onChange]
  );

  const handleChargeOnChange = useCallback(
    (keys: Selection): void => {
      if (keys === 'all') return;
      const value = Array.from(keys)[0] as CommissionTiming | undefined;
      if (value) emitChange({ chargeOn: value });
    },
    [emitChange]
  );

  // ---------------------------------------------------------------------------
  // Tier Handlers
  // ---------------------------------------------------------------------------

  const handleTierChange = useCallback(
    (index: number, field: keyof CommissionTier, value: number | string | undefined): void => {
      if (!commission.tiers) return;
      const updatedTiers = commission.tiers.map((tier, i) => {
        if (i !== index) return tier;
        if (value === undefined) {
          const { [field]: _, ...rest } = tier;
          return rest as CommissionTier;
        }
        return { ...tier, [field]: value };
      });
      emitChange({ tiers: updatedTiers });
    },
    [commission.tiers, emitChange]
  );

  const handleAddTier = useCallback((): void => {
    const currentTiers = commission.tiers ?? [];
    const tierPrecision = currentTiers[0]?.feePrecision
      ?? commission.minPerOrderPrecision ?? commission.maxPerOrderPrecision ?? 2;
    emitChange({
      tiers: [...currentTiers, { threshold: 0, feeAmount: 0, feePrecision: tierPrecision }],
    });
  }, [commission.tiers, commission.minPerOrderPrecision, commission.maxPerOrderPrecision, emitChange]);

  const handleRemoveTier = useCallback(
    (index: number): void => {
      if (!commission.tiers || commission.tiers.length <= 1) return;
      emitChange({ tiers: commission.tiers.filter((_, i) => i !== index) });
    },
    [commission.tiers, emitChange]
  );

  // ---------------------------------------------------------------------------
  // Verify Handler
  // ---------------------------------------------------------------------------

  const handleVerify = useCallback((): void => {
    emitChange({
      verificationStatus: 'userVerified',
      lastVerifiedAt: new Date().toISOString(),
    });
  }, [emitChange]);

  // ---------------------------------------------------------------------------
  // Precision Handlers
  // ---------------------------------------------------------------------------

  /** Unified precision change — applies to all applicable fields based on feeType */
  const handlePrecisionChange = useCallback(
    (newPrecision: number): void => {
      const { feeType: ft } = commission;
      const changes: LoosePartial<typeof commission> = {};

      // Min/Max Per Order — always updated for non-spreadOnly types
      const oldMinP = commission.minPerOrderPrecision ?? 2;
      changes.minPerOrderPrecision = newPrecision;
      changes.minPerOrder = rescaleForPrecision(commission.minPerOrder, oldMinP, newPrecision);

      const oldMaxP = commission.maxPerOrderPrecision ?? 2;
      changes.maxPerOrderPrecision = newPrecision;
      changes.maxPerOrder = rescaleForPrecision(commission.maxPerOrder, oldMaxP, newPrecision);

      if (ft === 'tiered') {
        // Rescale all tier fee amounts
        if (commission.tiers) {
          changes.tiers = commission.tiers.map((tier) => {
            const oldTP = tier.feePrecision ?? 2;
            return {
              ...tier,
              feePrecision: newPrecision,
              feeAmount: rescaleForPrecision(tier.feeAmount, oldTP, newPrecision) ?? 0,
            };
          });
        }
      } else {
        // Fee Amount (fixedPerUnit, fixedPerTransaction, percentage, perShare, perSharePlusBase)
        if (TYPES_WITH_FEE_AMOUNT.has(ft)) {
          const oldFP = commission.feePrecision ?? 2;
          changes.feePrecision = newPrecision;
          changes.feeAmount = rescaleForPrecision(commission.feeAmount, oldFP, newPrecision);
        }

        // Base Fee (perSharePlusBase)
        if (ft === 'perSharePlusBase') {
          const oldBP = commission.baseFeePrecision ?? 2;
          changes.baseFeePrecision = newPrecision;
          changes.baseFee = rescaleForPrecision(commission.baseFee, oldBP, newPrecision);
        }
      }

      emitChange(changes);
    },
    [commission, emitChange]
  );

  /** Maker/Taker precision change — split into "fees" vs "order limits" */
  const handleMakerTakerPrecisionChange = useCallback(
    (group: 'fees' | 'orderLimits', newPrecision: number): void => {
      const changes: LoosePartial<typeof commission> = {};

      if (group === 'fees') {
        // Both maker and taker fees use the same precision
        const oldMP = commission.makerFeePrecision ?? 2;
        changes.makerFeePrecision = newPrecision;
        changes.makerFee = rescaleForPrecision(commission.makerFee, oldMP, newPrecision);

        const oldTP = commission.takerFeePrecision ?? 2;
        changes.takerFeePrecision = newPrecision;
        changes.takerFee = rescaleForPrecision(commission.takerFee, oldTP, newPrecision);
      } else {
        // Min/Max per order
        const oldMinP = commission.minPerOrderPrecision ?? 2;
        changes.minPerOrderPrecision = newPrecision;
        changes.minPerOrder = rescaleForPrecision(commission.minPerOrder, oldMinP, newPrecision);

        const oldMaxP = commission.maxPerOrderPrecision ?? 2;
        changes.maxPerOrderPrecision = newPrecision;
        changes.maxPerOrder = rescaleForPrecision(commission.maxPerOrder, oldMaxP, newPrecision);
      }

      emitChange(changes);
    },
    [commission, emitChange]
  );

  // ---------------------------------------------------------------------------
  // Derived State
  // ---------------------------------------------------------------------------

  const { feeType } = commission;
  const showFeeAmount = TYPES_WITH_FEE_AMOUNT.has(feeType);
  const showChargeOn = TYPES_WITH_ORDER_FIELDS.has(feeType);
  const showMinMax = TYPES_WITH_ORDER_FIELDS.has(feeType);
  const showBaseFee = feeType === 'perSharePlusBase';
  const showTiers = feeType === 'tiered';
  const showMakerTaker = feeType === 'makerTaker';
  const canVerify =
    needsVerification(commission.verificationStatus) ||
    commission.verificationStatus === 'verified';

  /** Whether the Fee Structure card has any visible fields */
  const hasFeeStructureFields = showFeeAmount || showBaseFee || showMakerTaker || showTiers || showMinMax;

  /** Current precision — single derived value for popover selectedKeys and new-tier default */
  const currentPrecision = showTiers
    ? commission.tiers?.[0]?.feePrecision ?? commission.minPerOrderPrecision ?? commission.maxPerOrderPrecision ?? 2
    : commission.feePrecision ?? 2;

  /** Currency hint for currency-denominated fields; fallback when no currency set */
  const currencyHint = accountCurrency ?? 'Integer amount';
  /** Hint for basis-point fields (percentage feeAmount, maker/taker fees) */
  const bpsHint = 'Basis points';

  // ---------------------------------------------------------------------------
  // Inherited Mode (Read-Only Display)
  // ---------------------------------------------------------------------------

  if (mode === 'inherited') {
    return (
      <div className="flex flex-col gap-3 p-3">
        {/* Fee Type Card: Commission Type + Charge On */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Fee Type
          </CardHeader>
          <CardBody className="px-1 pb-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className={!showChargeOn ? 'md:col-span-2' : undefined}>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-text-secondary">Commission Type</span>
                  <span className="text-base md:text-sm text-text-primary">
                    {COMMISSION_TYPE_DISPLAY_MAP[commission.feeType]}
                  </span>
                </div>
              </div>
              {showChargeOn && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-text-secondary">Charge On</span>
                  <span className="text-base md:text-sm text-text-primary">
                    {COMMISSION_TIMING_DISPLAY_MAP[commission.chargeOn]}
                  </span>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Fee Structure Card */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Fee Structure
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            {!hasFeeStructureFields ? (
              <p className="text-sm text-text-tertiary py-2">
                Spread-based — fee is embedded in the bid-ask spread
              </p>
            ) : (
              <>
                {/* Fee Amount + Base Fee (perSharePlusBase pairs them) */}
                {showFeeAmount && showBaseFee && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-text-secondary">
                        {FEE_AMOUNT_LABEL_MAP[feeType] ?? 'Fee Amount'}
                      </span>
                      <span className="text-base md:text-sm text-text-primary">
                        {formatFeeAmount(commission.feeAmount, commission.feePrecision)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-text-secondary">Base Fee</span>
                      <span className="text-base md:text-sm text-text-primary">
                        {formatFeeAmount(commission.baseFee, commission.baseFeePrecision)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Fee Amount standalone (non-perSharePlusBase) */}
                {showFeeAmount && !showBaseFee && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-text-secondary">
                      {FEE_AMOUNT_LABEL_MAP[feeType] ?? 'Fee Amount'}
                    </span>
                    <span className="text-base md:text-sm text-text-primary">
                      {formatFeeAmount(commission.feeAmount, commission.feePrecision)}
                    </span>
                  </div>
                )}

                {/* Maker/Taker Fees */}
                {showMakerTaker && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-text-secondary">Maker Fee</span>
                      <span className="text-base md:text-sm text-text-primary">
                        {formatFeeAmount(commission.makerFee, commission.makerFeePrecision)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-text-secondary">Taker Fee</span>
                      <span className="text-base md:text-sm text-text-primary">
                        {formatFeeAmount(commission.takerFee, commission.takerFeePrecision)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Tiers (read-only table) */}
                {showTiers && commission.tiers && commission.tiers.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-text-secondary">Pricing Tiers</span>
                    <div className="border border-default-200 rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-default-50">
                            <th className="text-left px-2 py-1 text-xs font-medium text-text-secondary">Threshold</th>
                            <th className="text-left px-2 py-1 text-xs font-medium text-text-secondary">Fee Amount</th>
                            <th className="text-left px-2 py-1 text-xs font-medium text-text-secondary">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commission.tiers.map((tier, i) => (
                            <tr key={i} className="border-t border-default-100">
                              <td className="px-2 py-1">{tier.threshold}</td>
                              <td className="px-2 py-1">{formatFeeAmount(tier.feeAmount, tier.feePrecision)}</td>
                              <td className="px-2 py-1 text-text-secondary">{tier.description ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Min/Max Per Order */}
                {showMinMax && (commission.minPerOrder !== undefined || commission.maxPerOrder !== undefined) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {commission.minPerOrder !== undefined && (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-text-secondary">Min Per Order</span>
                        <span className="text-base md:text-sm text-text-primary">
                          {formatFeeAmount(commission.minPerOrder, commission.minPerOrderPrecision)}
                        </span>
                      </div>
                    )}
                    {commission.maxPerOrder !== undefined && (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-text-secondary">Max Per Order</span>
                        <span className="text-base md:text-sm text-text-primary">
                          {formatFeeAmount(commission.maxPerOrder, commission.maxPerOrderPrecision)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* Verification Card */}
        {!hideVerification && (
          <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
            <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
              Verification
            </CardHeader>
            <CardBody className="px-1 pb-0 space-y-3">
              <div className="flex items-center gap-2">
                <Chip
                  size="sm"
                  variant="flat"
                  color={VERIFICATION_STATUS_COLOR_MAP[commission.verificationStatus]}
                >
                  {VERIFICATION_STATUS_DISPLAY_MAP[commission.verificationStatus]}
                </Chip>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Last Verified</span>
                <span className="text-base md:text-sm text-text-primary">
                  {commission.lastVerifiedAt
                    ? new Date(commission.lastVerifiedAt).toLocaleDateString()
                    : 'Not verified'}
                </span>
              </div>
              {commission.verificationNotes && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-text-secondary">Verification Notes</span>
                  <span className="text-base md:text-sm text-text-primary">{commission.verificationNotes}</span>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit Mode
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Fee Type Card: Commission Type + Charge On */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          Fee Type
        </CardHeader>
        <CardBody className="px-1 pb-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Commission Type"
              labelPlacement="outside-top"
              selectedKeys={[commission.feeType]}
              onSelectionChange={handleTypeChange}
              size="sm"
              className={!showChargeOn ? 'md:col-span-2' : ''}
              classNames={{ value: 'text-base md:text-sm' }}
            >
              {(Object.entries(COMMISSION_TYPE_DISPLAY_MAP) as [CommissionType, string][]).map(
                ([key, label]) => (
                  <SelectItem key={key}>{label}</SelectItem>
                )
              )}
            </Select>
            {showChargeOn && (
              <Select
                label="Charge On"
                labelPlacement="outside-top"
                selectedKeys={[commission.chargeOn]}
                onSelectionChange={handleChargeOnChange}
                size="sm"
                classNames={{ value: 'text-base md:text-sm' }}
              >
                {(Object.entries(COMMISSION_TIMING_DISPLAY_MAP) as [CommissionTiming, string][]).map(
                  ([key, label]) => (
                    <SelectItem key={key} description={COMMISSION_TIMING_DESCRIPTION_MAP[key]}>{label}</SelectItem>
                  )
                )}
              </Select>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Fee Structure Card */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span>Fee Structure</span>
            {hasFeeStructureFields && (
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Button size="sm" variant="light" startContent={<Settings2 size={16} />}>
                    Precision
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="w-52 flex flex-col gap-3">
                    <p className="text-xs font-medium text-text-secondary">
                      Decimal Precision
                    </p>
                    {showMakerTaker ? (
                      <>
                        <Select
                          label="Maker/Taker Fee"
                          labelPlacement="outside"
                          size="sm"
                          selectedKeys={[String(commission.makerFeePrecision ?? 2)]}
                          onSelectionChange={(keys) => {
                            if (keys === 'all') return;
                            const val = Array.from(keys)[0] as string | undefined;
                            if (val !== undefined) handleMakerTakerPrecisionChange('fees', Number(val));
                          }}
                          classNames={{ value: 'text-base md:text-sm' }}
                        >
                          {PRECISION_OPTIONS.map((opt) => (
                            <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </Select>
                        <Select
                          label="Min/Max Per Order"
                          labelPlacement="outside"
                          size="sm"
                          // Shows minPerOrderPrecision; handler always writes both min+max
                          // together, so they stay in sync. Legacy divergence corrected on first change.
                          selectedKeys={[String(commission.minPerOrderPrecision ?? 2)]}
                          onSelectionChange={(keys) => {
                            if (keys === 'all') return;
                            const val = Array.from(keys)[0] as string | undefined;
                            if (val !== undefined) handleMakerTakerPrecisionChange('orderLimits', Number(val));
                          }}
                          classNames={{ value: 'text-base md:text-sm' }}
                        >
                          {PRECISION_OPTIONS.map((opt) => (
                            <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <Select
                        label="Decimal Places"
                        labelPlacement="outside"
                        size="sm"
                        selectedKeys={[String(currentPrecision)]}
                        onSelectionChange={(keys) => {
                          if (keys === 'all') return;
                          const val = Array.from(keys)[0] as string | undefined;
                          if (val !== undefined) handlePrecisionChange(Number(val));
                        }}
                        classNames={{ value: 'text-base md:text-sm' }}
                      >
                        {PRECISION_OPTIONS.map((opt) => (
                          <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </Select>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CardHeader>
        <CardBody className="px-1 pb-0 space-y-3">
          {!hasFeeStructureFields ? (
            <p className="text-sm text-text-tertiary py-2">
              Spread-based — fee is embedded in the bid-ask spread
            </p>
          ) : (
            <>
              {/* Fee Amount + Base Fee (perSharePlusBase pairs them in grid) */}
              {showFeeAmount && showBaseFee && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <NumberInput
                    label={FEE_AMOUNT_LABEL_MAP[feeType] ?? 'Fee Amount'}
                    labelPlacement="outside"
                    endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
                    value={toDisplayValue(commission.feeAmount, commission.feePrecision)}
                    onValueChange={(val) => {
                      const changes: LoosePartial<typeof commission> = { feeAmount: toStorageValue(val, commission.feePrecision) };
                      if (commission.feePrecision === undefined) {
                        changes.feePrecision = 2;
                      }
                      emitChange(changes);
                    }}
                    formatOptions={{ minimumFractionDigits: commission.feePrecision ?? 2, maximumFractionDigits: commission.feePrecision ?? 2 }}
                    size="sm"
                    hideStepper
                    isWheelDisabled
                    classNames={{ input: 'text-base md:text-sm text-right' }}
                    placeholder="0"
                  />
                  <NumberInput
                    label="Base Fee"
                    labelPlacement="outside"
                    endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
                    value={toDisplayValue(commission.baseFee, commission.baseFeePrecision)}
                    onValueChange={(val) => {
                      const changes: LoosePartial<typeof commission> = { baseFee: toStorageValue(val, commission.baseFeePrecision) };
                      if (commission.baseFeePrecision === undefined) {
                        changes.baseFeePrecision = 2;
                      }
                      emitChange(changes);
                    }}
                    formatOptions={{ minimumFractionDigits: commission.baseFeePrecision ?? 2, maximumFractionDigits: commission.baseFeePrecision ?? 2 }}
                    size="sm"
                    hideStepper
                    isWheelDisabled
                    classNames={{ input: 'text-base md:text-sm text-right' }}
                    placeholder="0"
                  />
                </div>
              )}

              {/* Fee Amount standalone (non-perSharePlusBase) */}
              {showFeeAmount && !showBaseFee && (
                <NumberInput
                  label={FEE_AMOUNT_LABEL_MAP[feeType] ?? 'Fee Amount'}
                  labelPlacement="outside"
                  endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{feeType === 'percentage' ? bpsHint : currencyHint}</span>}
                  value={toDisplayValue(commission.feeAmount, commission.feePrecision)}
                  onValueChange={(val) => {
                    const changes: LoosePartial<typeof commission> = { feeAmount: toStorageValue(val, commission.feePrecision) };
                    if (feeType !== 'percentage' && commission.feePrecision === undefined) {
                      changes.feePrecision = 2;
                    }
                    emitChange(changes);
                  }}
                  formatOptions={{ minimumFractionDigits: commission.feePrecision ?? 2, maximumFractionDigits: commission.feePrecision ?? 2 }}
                  size="sm"
                  hideStepper
                  isWheelDisabled
                  classNames={{ input: 'text-base md:text-sm text-right' }}
                  placeholder="0"
                />
              )}

              {/* Maker/Taker Fees */}
              {showMakerTaker && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <NumberInput
                    label="Maker Fee"
                    labelPlacement="outside"
                    endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{bpsHint}</span>}
                    value={toDisplayValue(commission.makerFee, commission.makerFeePrecision)}
                    onValueChange={(val) => emitChange({ makerFee: toStorageValue(val, commission.makerFeePrecision) })}
                    formatOptions={{ minimumFractionDigits: commission.makerFeePrecision ?? 2, maximumFractionDigits: commission.makerFeePrecision ?? 2 }}
                    size="sm"
                    hideStepper
                    isWheelDisabled
                    classNames={{ input: 'text-base md:text-sm text-right' }}
                    placeholder="0"
                  />
                  <NumberInput
                    label="Taker Fee"
                    labelPlacement="outside"
                    endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{bpsHint}</span>}
                    value={toDisplayValue(commission.takerFee, commission.takerFeePrecision)}
                    onValueChange={(val) => emitChange({ takerFee: toStorageValue(val, commission.takerFeePrecision) })}
                    formatOptions={{ minimumFractionDigits: commission.takerFeePrecision ?? 2, maximumFractionDigits: commission.takerFeePrecision ?? 2 }}
                    size="sm"
                    hideStepper
                    isWheelDisabled
                    classNames={{ input: 'text-base md:text-sm text-right' }}
                    placeholder="0"
                  />
                </div>
              )}

              {/* Tiered Pricing */}
              {showTiers && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-medium text-text-secondary">Pricing Tiers</span>
                  {(commission.tiers ?? []).map((tier, index) => (
                    <div key={index} className="grid grid-cols-2 gap-3">
                      <div className="flex items-end gap-2">
                        <NumberInput
                          label={index === 0 ? 'Threshold' : undefined}
                          aria-label="Tier threshold"
                          labelPlacement="outside"
                          endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
                          value={tier.threshold}
                          onValueChange={(val) => handleTierChange(index, 'threshold', val ?? 0)}
                          size="sm"
                          hideStepper
                          isWheelDisabled
                          classNames={{ input: 'text-base md:text-sm text-right' }}
                          className="flex-1"
                          placeholder="0"
                        />
                        <NumberInput
                          label={index === 0 ? 'Fee Amount' : undefined}
                          aria-label="Tier fee amount"
                          labelPlacement="outside"
                          endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
                          value={toDisplayValue(tier.feeAmount, tier.feePrecision)}
                          onValueChange={(val) => handleTierChange(index, 'feeAmount', toStorageValue(val, tier.feePrecision) ?? 0)}
                          formatOptions={{ minimumFractionDigits: tier.feePrecision ?? 2, maximumFractionDigits: tier.feePrecision ?? 2 }}
                          size="sm"
                          hideStepper
                          isWheelDisabled
                          classNames={{ input: 'text-base md:text-sm text-right' }}
                          className="flex-1"
                          placeholder="0"
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <Input
                          label={index === 0 ? 'Description' : undefined}
                          aria-label="Tier description"
                          labelPlacement="outside"
                          value={tier.description ?? ''}
                          onValueChange={(val) =>
                            handleTierChange(index, 'description', val || undefined)
                          }
                          size="sm"
                          classNames={{ input: 'text-base md:text-sm' }}
                          className="flex-1"
                          placeholder="Optional"
                        />
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          color="danger"
                          aria-label="Remove tier"
                          isDisabled={(commission.tiers?.length ?? 0) <= 1}
                          onPress={() => handleRemoveTier(index)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="flat"
                    startContent={<Plus size={14} />}
                    onPress={handleAddTier}
                    className="self-start"
                  >
                    Add Tier
                  </Button>
                </div>
              )}

              {/* Min/Max Per Order */}
              {showMinMax && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <NumberInput
                    label="Min Per Order"
                    labelPlacement="outside"
                    endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
                    value={toDisplayValue(commission.minPerOrder, commission.minPerOrderPrecision)}
                    onValueChange={(val) => {
                      const changes: LoosePartial<typeof commission> = { minPerOrder: toStorageValue(val, commission.minPerOrderPrecision) };
                      if (commission.minPerOrderPrecision === undefined) {
                        changes.minPerOrderPrecision = 2;
                      }
                      emitChange(changes);
                    }}
                    formatOptions={{ minimumFractionDigits: commission.minPerOrderPrecision ?? 2, maximumFractionDigits: commission.minPerOrderPrecision ?? 2 }}
                    size="sm"
                    hideStepper
                    isWheelDisabled
                    classNames={{ input: 'text-base md:text-sm text-right' }}
                    placeholder="Optional"
                  />
                  <NumberInput
                    label="Max Per Order"
                    labelPlacement="outside"
                    endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
                    value={toDisplayValue(commission.maxPerOrder, commission.maxPerOrderPrecision)}
                    onValueChange={(val) => {
                      const changes: LoosePartial<typeof commission> = { maxPerOrder: toStorageValue(val, commission.maxPerOrderPrecision) };
                      if (commission.maxPerOrderPrecision === undefined) {
                        changes.maxPerOrderPrecision = 2;
                      }
                      emitChange(changes);
                    }}
                    formatOptions={{ minimumFractionDigits: commission.maxPerOrderPrecision ?? 2, maximumFractionDigits: commission.maxPerOrderPrecision ?? 2 }}
                    size="sm"
                    hideStepper
                    isWheelDisabled
                    classNames={{ input: 'text-base md:text-sm text-right' }}
                    placeholder="Optional"
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Verification Card */}
      {!hideVerification && (
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            <div className="flex items-center justify-between w-full">
              <span>Verification</span>
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    color={commission.verificationNotes ? 'primary' : 'default'}
                    startContent={<NotepadText size={16} />}
                  >
                    Notes
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="w-72 flex flex-col gap-2">
                    <p className="text-xs font-medium text-text-secondary">
                      Verification Notes
                    </p>
                    <TextArea
                      value={commission.verificationNotes ?? ''}
                      onValueChange={(val) => emitChange({ verificationNotes: val || undefined })}
                      size="sm"
                      classNames={{ input: 'text-base md:text-sm' }}
                      placeholder="Notes about fee verification or discrepancies..."
                      minRows={3}
                      maxRows={8}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Chip
                  size="sm"
                  variant="flat"
                  color={VERIFICATION_STATUS_COLOR_MAP[commission.verificationStatus]}
                >
                  {VERIFICATION_STATUS_DISPLAY_MAP[commission.verificationStatus]}
                </Chip>
              </div>
              {canVerify && (
                <Button
                  size="sm"
                  variant="flat"
                  color="success"
                  startContent={<ShieldCheck size={14} />}
                  onPress={handleVerify}
                >
                  Verify
                </Button>
              )}
            </div>

            <p className="text-xs text-text-secondary">
              Last verified:{' '}
              {commission.lastVerifiedAt
                ? new Date(commission.lastVerifiedAt).toLocaleDateString()
                : 'Not verified'}
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default CommissionTabContent;
